import McmEditingConfig from '~/tenants/mcm/performable/editing/McmEditingConfig';
import z from 'zod';
import {
  coerceBoolean,
  coerceLiteralNumberRequired,
  coerceOptionalPositiveNumber,
  FIRST_VERSION_TIMESTAMP,
  ZodVersionedMetadata
} from '~/lib/zod';
import type { CustomerOrderMetadataPolicy } from '~/lib/customer-order-metadata';
import { TenantOrderContext } from '~/tenants/common/TenantOrderContext';
import { TenantOrder } from '~/tenants/common/TenantOrder';
import McmPhotoConfig from '~/tenants/mcm/performable/photo/McmPhotoConfig';
import McmVirtualConfig from '~/tenants/mcm/performable/virtual/McmVirtualConfig';
import McmDroneConfig from '~/tenants/mcm/performable/drone/McmDroneConfig';
import McmVideoConfig from '~/tenants/mcm/performable/video/McmVideoConfig';
import McmFloorplanConfig from '~/tenants/mcm/performable/floorplan/McmFloorplanConfig';

import McmZillowConfig from '~/tenants/mcm/performable/zillow/McmZillowConfig';
import McmMatterportConfig from '~/tenants/mcm/performable/matterport/McmMatterportConfig';
import { McmCustomerMetadata } from '~/tenants/mcm/model/McmCustomer';
import { McmProviderStaticData } from '~/tenants/mcm/model/McmProvider';
import McmSlideshowConfig, { McmSlideshowOrientation } from '~/tenants/mcm/performable/slideshow/McmSlideshowConfig';
import { SLIDESHOW_VARIANTS_CUTOFF_MS, SlideshowFeatures } from '~common/model/Slideshow';
import McmSlideshowJob from '~/tenants/mcm/performable/slideshow/McmSlideshowJob';
import { TenantOrderLine } from '~/lib/model';
import { AppointmentCancel } from '~/lib/enum';
import { datetime, formalTime } from '~/lib/datettime';
import Big from 'big.js';
import McmSoldConfig from '~/tenants/mcm/performable/sold/McmSoldConfig';
import McmCustomConfig from '~/tenants/mcm/performable/custom/McmCustomConfig';
import McmCustomJob from '~/tenants/mcm/performable/custom/McmCustomJob';
import McmStagingConfig from '~/tenants/mcm/performable/staging/McmStagingConfig';
import McmQuickConfig from '~/tenants/mcm/performable/quick/McmQuickConfig';

export enum McmOrderType {
  RESIDENTIAL = 'residential',
  RENTAL = 'rental',
}

export enum McmPropertyType {
  SINGLE_FAMILY_HOME = 'single_family_home',
  CONDO = 'condo',
  TOWNHOME = 'townhome',
  MOBILE_HOME = 'mobile_home',
  MULTI_FAMILY = 'multi_family',
  LAND_ONLY = 'land_only',
}

const schema = z.object({
  version: coerceLiteralNumberRequired(FIRST_VERSION_TIMESTAMP),
  type: z.nativeEnum(McmOrderType),
  property: z.nativeEnum(McmPropertyType).optional(),
  special: z.string().optional(),
  terms: coerceBoolean().refine((b) => b, 'Terms must be accepted.'),
  entry: z.string().optional(),
  vacant: coerceBoolean().optional(),
  sqft: z.coerce
    .number({ required_error: 'Square feet is required.', invalid_type_error: 'Square feet is required.' })
    .positive({ message: 'Square feet must be a positive number.' }),
  price: coerceOptionalPositiveNumber('Listing price must be a positive number.'),
});

export const McmOrderSchema = {
  [FIRST_VERSION_TIMESTAMP]: schema,
};

export type McmOrderMetadata = ZodVersionedMetadata<typeof McmOrderSchema>;

/**
 * The order fields an MCM customer may write (lib/customer-order-metadata.ts):
 * all of them. McmOrderForm shows the office nothing a customer does not see
 * (`price` only for a Crossroads buyer, and it prices nothing).
 */
export const MCM_CUSTOMER_ORDER_METADATA: CustomerOrderMetadataPolicy = {
  customer: ['type', 'property', 'special', 'terms', 'entry', 'vacant', 'sqft', 'price'],
  office: [],
};

export type McmPerformableConfig =
  | typeof McmPhotoConfig
  | typeof McmVirtualConfig
  | typeof McmDroneConfig
  | typeof McmVideoConfig
  | typeof McmFloorplanConfig
  | typeof McmZillowConfig
  | typeof McmSlideshowConfig
  | typeof McmSoldConfig
  | typeof McmCustomConfig
  | typeof McmQuickConfig
  | typeof McmStagingConfig
  | typeof McmMatterportConfig
  | typeof McmEditingConfig;

export type McmOrderContext = TenantOrderContext<
  McmOrderMetadata,
  McmPerformableConfig,
  McmCustomerMetadata,
  McmProviderStaticData
>;

/**
 * MCM's cancellation fees (its order form; founder, 9/26): a share of the
 * cancelled visit's booked services — 15% once it is after 6:30 PM the day
 * before (the office's SAME_DAY stage, and what the change policy bills a
 * late customer change as), 50% once the photographer is on the way or there.
 */
export const MCM_CANCEL_FEE_SHARE: Record<AppointmentCancel, string> = {
  [AppointmentCancel.SAME_DAY]: '0.15',
  [AppointmentCancel.ENROUTE]: '0.5',
  [AppointmentCancel.ONSITE]: '0.5',
};

/** Orders placed before this keep the old behaviour: the office priced their cancellations by hand. */
export const MCM_CANCEL_FEES_FROM = '2026-09-26T16:00:00.000Z';

export class McmOrder extends TenantOrder<McmOrderContext> {
  get microsite() {
    return !this.buyer('crossroads');
  }

  revenueLines(): TenantOrderLine[] {
    const lines = super.revenueLines();

    if (new Date(this.context.created) >= new Date(MCM_CANCEL_FEES_FROM)) {
      lines.push(...this.cancellationLines(lines));
    }

    const pkg = this.package();

    if (pkg && !this.buyer('crossroads')) {
      lines.push({
        id: 'package_discount',
        description: `${pkg.config.name} Package Discount`,
        amount: pkg.amount,
        discount: true,
      });
    }

    return lines;
  }

  /** One fee line per cancelled appointment: its share of the services that visit was booked for. */
  private cancellationLines(serviceLines: TenantOrderLine[]): TenantOrderLine[] {
    const lines: TenantOrderLine[] = [];

    for (const appointment of this.context.appointments) {
      if (!appointment.canceled) {
        continue;
      }

      const services = serviceLines
        .filter((line) => line.job_id && appointment.job_ids.includes(line.job_id) && !line.fee && !line.discount)
        .reduce((sum, line) => sum.plus(line.amount), new Big(0));
      const amount = services.times(MCM_CANCEL_FEE_SHARE[appointment.canceled]).round(2);

      if (amount.gt(0)) {
        lines.push({
          id: `cancel-${appointment.id}`,
          description: `Cancellation Fee (${formalTime(datetime(appointment.start, this.location.timezone))})`,
          appointment_id: appointment.id,
          amount,
        });
      }
    }

    return lines;
  }

  get slideshows(): SlideshowFeatures | null {
    const bought = this.slideshowLine();

    if (!this.packageSlideshow()) {
      return bought;
    }

    // A package that includes the 16:9 (the Sky-to-Door Tour, 9/30/26): the
    // BRANDED 16:9 only, at no charge (founder, 9/30: the listing leads with
    // the flythrough film, so it needs no MLS-safe slideshow). The Photology
    // property page never plays a branded video (micrositeSlideshowSkip), so it lists no
    // slideshow video and its hero stays the live player drawn from the
    // photos, as on Twist's V14 orders. A customer with no agent profile gets
    // none until they add one, as on Twist V14: the Deliverables page asks
    // for it, and its order branding page marks the order dirty.
    //
    // A Slideshow line bought on top: Horizontal keeps its unbranded 16:9;
    // Vertical or Both come out exactly as a Both line (the unbranded and
    // branded 16:9 and the branded 9:16; the unbranded 9:16 only with no
    // profile to brand one, slideshowVariants). The $16 reel is sold
    // and unlocked exactly as on a Horizontal line: `vertical` stays off
    // until it is paid (slideshowLine already folds the reel into a line).
    return {
      branded: true,
      unbranded: false,
      autoSelect: true,
      ...bought,
      horizontal: true,
      vertical: bought ? bought.vertical : this.context.reelPaid === true,
      inPackage: true,
    };
  }

  /**
   * Whether a package line on this order includes the 16:9 slideshow: a
   * custom line carrying `slideshow` (bleenk.quote.ts, the Sky-to-Door
   * tier). Only lines booked once that shipped carry it, so no earlier
   * order gains a video. Read off the LINE, not off the whole package
   * (orderIsSkyToDoor): the slideshow is made of the photos, so an office
   * that takes the drone off (no-fly zone) takes the flythrough and the
   * waypoint still with it but leaves the slideshow the customer paid for.
   * Not for Crossroads, as a Slideshow line is not (McmSlideshowJob.eligible).
   */
  private packageSlideshow(): boolean {
    return (
      this.jobs.some((job) => job instanceof McmCustomJob && Boolean(job.metadata.package) && job.metadata.slideshow === true) &&
      !this.buyer('crossroads')
    );
  }

  /** The videos a Slideshow line on the order buys, or null with none. */
  private slideshowLine(): SlideshowFeatures | null {
    for (const job of this.jobs) {
      if (job instanceof McmSlideshowJob) {
        const { orientation } = job.metadata;

        // Bought from the Deliveries page after delivery, exactly as in
        // TwtOrder. Without this term the money and the video came apart:
        // reelPanel offered the reel whenever `vertical` was false, so an
        // agent who ordered HORIZONTAL was shown the $16, paid, and got
        // `reel_status: 'paid'` — but `vertical` stayed false here, no
        // portrait variant was ever created, and nothing rendered. The
        // refund hook only fires on a render FAILURE, and there was no
        // render to fail, so the charge stood and nothing was delivered
        // (9/22/26). reel-deliverable.ts now asks this getter, with the
        // reel paid, before anything is sold.
        //
        // It applies to the pre-cutoff branch too. That branch keeps the
        // old swapped mapping so delivered orders are not re-cut, but a
        // reel bought today is not a re-cut — it is a new variant beside
        // the existing one, and it was paid for on purpose.
        const reel = this.context.reelPaid === true;

        // The mapping was swapped — a horizontal purchase produced a
        // portrait video — and "both" produced nothing at all. Correcting
        // it changes what an order WANTS, and nothing deletes a video for
        // an orientation that is no longer wanted, so applying it to past
        // orders would queue a fresh render for the whole MCM back
        // catalogue and leave two differently shaped videos in every
        // gallery. Orders placed before the cutoff therefore keep the old,
        // wrong mapping: they have already been delivered.
        if (new Date(this.context.created).getTime() < SLIDESHOW_VARIANTS_CUTOFF_MS) {
          return {
            branded: true,
            horizontal: orientation === McmSlideshowOrientation.VERTICAL,
            vertical: orientation === McmSlideshowOrientation.HORIZONTAL || reel,
            unbranded: true,
          };
        }

        return {
          branded: true,
          horizontal:
            orientation === McmSlideshowOrientation.HORIZONTAL || orientation === McmSlideshowOrientation.BOTH,
          vertical:
            orientation === McmSlideshowOrientation.VERTICAL ||
            orientation === McmSlideshowOrientation.BOTH ||
            reel,
          unbranded: true,
          // MCM is the first tenant on automatic photo selection. An
          // order with more photos than slides gets them chosen and put
          // in walkthrough order instead of taking the first 20 by
          // filename; an agent who has arranged the slideshow themselves
          // is never overridden (shouldAutoSelectSlideshow).
          autoSelect: true,
        };
      }
    }

    return null;
  }
}
